import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Skeleton,
} from '@mui/material';
import { Add, AccessTime, Flag, InboxOutlined, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Task, TaskStatus, TaskPriority } from '../../types/common';
import TaskDetail from '../Tasks/TaskDetail';
import './KanbanBoard.scss';

interface KanbanBoardProps {
  tasks: Task[];
  onUpdateTask: (taskId: string, updates: Partial<Task>) => void;
  onCreateTask: (status: TaskStatus) => void;
  epics?: any[];
  canCreateTask?: boolean;
  initialTaskId?: string;
  onClearInitialTask?: () => void;
  loading?: boolean;
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({
  tasks,
  onUpdateTask,
  onCreateTask,
  epics = [],
  canCreateTask = true,
  initialTaskId,
  onClearInitialTask,
  loading = false,
}) => {
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [handledTaskId, setHandledTaskId] = useState<string | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<TaskStatus | null>(null);
  const [draggingTaskId, setDraggingTaskId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, tasks, loading]);

  const scrollColumns = (direction: 1 | -1) => {
    scrollRef.current?.scrollBy({ left: direction * 400, behavior: 'smooth' });
  };

  useEffect(() => {
    if (initialTaskId && initialTaskId !== handledTaskId && tasks && tasks.length > 0) {
      const taskToOpen = tasks.find((t) => t.id === initialTaskId);
      if (taskToOpen) {
        setSelectedTask(taskToOpen);
        setDetailOpen(true);
        setHandledTaskId(initialTaskId);
      }
    }

    if (!initialTaskId && handledTaskId) {
      setHandledTaskId(null);
    }
  }, [initialTaskId, tasks, handledTaskId]);

  const columns = [
    { status: TaskStatus.TODO, title: 'To Do', color: '#b9bec7' },
    { status: TaskStatus.IN_PROGRESS, title: 'In Progress', color: '#0073ea' },
    { status: TaskStatus.REVIEW, title: 'Review', color: '#f5b942' },
    { status: TaskStatus.TESTING, title: 'Testing', color: '#8b7fd6' },
    { status: TaskStatus.DONE, title: 'Done', color: '#22c58c' },
    { status: TaskStatus.BLOCKED, title: 'Blocked', color: '#f0576b' },
  ];

  const getTasksByStatus = (status: TaskStatus) => {
    return (tasks || []).filter((task) => task.status === status);
  };

  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case TaskPriority.URGENT:
        return 'error';
      case TaskPriority.HIGH:
        return 'warning';
      case TaskPriority.MEDIUM:
        return 'info';
      case TaskPriority.LOW:
        return 'default';
      default:
        return 'default';
    }
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    if (initialTaskId && onClearInitialTask) {
      onClearInitialTask();
    }
    setDetailOpen(false);
    setTimeout(() => {
      setSelectedTask(null);
    }, 300);
  };

  const handleDragStart = (e: React.DragEvent, task: Task) => {
    e.dataTransfer.setData('taskId', task.id);
    setDraggingTaskId(task.id);
  };

  const handleDragEnd = () => {
    setDraggingTaskId(null);
    setDragOverStatus(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDragEnter = (e: React.DragEvent, status: TaskStatus) => {
    e.preventDefault();
    setDragOverStatus(status);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // currentTarget is the column; relatedTarget is where the pointer is
    // headed next - only clear the highlight once we've actually left the
    // column (not just moved between two children inside it).
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverStatus(null);
    }
  };

  const handleDrop = (e: React.DragEvent, status: TaskStatus) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId');
    onUpdateTask(taskId, { status });
    setDragOverStatus(null);
    setDraggingTaskId(null);
  };

  const getTaskProgress = (task: Task) => {
    if (!task.subtasks || task.subtasks.length === 0) return null;
    const completed = task.subtasks.filter(st => st.completed).length;
    const total = task.subtasks.length;
    return { completed, total };
  };

  return (
    <Box className="kanban-board" ref={scrollRef}>
      {canScrollLeft && (
        <IconButton
          className="kanban-board__scroll-btn kanban-board__scroll-btn--left"
          onClick={() => scrollColumns(-1)}
          aria-label="Scroll columns left"
        >
          <ChevronLeft />
        </IconButton>
      )}
      <Box className="kanban-board__columns">
        {columns.map((column) => {
          const columnTasks = getTasksByStatus(column.status);
          return (
            <Box
              key={column.status}
              className={`kanban-column${dragOverStatus === column.status ? ' kanban-column--drag-over' : ''}`}
              style={{ '--accent': column.color } as React.CSSProperties}
              onDragOver={handleDragOver}
              onDragEnter={(e) => handleDragEnter(e, column.status)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, column.status)}
            >
              <Box className="kanban-column__header" style={{ borderTopColor: column.color }}>
                <Typography variant="h6" className="kanban-column__title">
                  {column.title}
                </Typography>
                <Chip
                  label={loading ? '–' : columnTasks.length}
                  size="small"
                  className="kanban-column__count"
                />
                {canCreateTask && (
                  <IconButton size="small" onClick={() => onCreateTask(column.status)}>
                    <Add fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Box className="kanban-column__content">
                {loading && (
                  <>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Box key={i} className="kanban-card-skeleton">
                        <Skeleton variant="rounded" width="40%" height={14} sx={{ mb: 1.5 }} />
                        <Skeleton variant="rounded" width="80%" height={18} sx={{ mb: 1 }} />
                        <Skeleton variant="rounded" width="95%" height={14} sx={{ mb: 0.75 }} />
                        <Skeleton variant="rounded" width="60%" height={14} sx={{ mb: 1.5 }} />
                        <Skeleton variant="rounded" width="100%" height={28} />
                      </Box>
                    ))}
                  </>
                )}

                {!loading && columnTasks.map((task) => {
                  const progress = getTaskProgress(task);
                  return (
                    <Card
                      key={task.id}
                      className={`kanban-card${draggingTaskId === task.id ? ' kanban-card--dragging' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, task)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleTaskClick(task)}
                    >
                      <CardContent className="kanban-card__content">
                        {/* Task key (e.g. AE-12) */}
                        {task.key && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            className="kanban-card__key"
                            sx={{ display: 'block', fontWeight: 600 }}
                          >
                            {task.key}
                          </Typography>
                        )}

                        {/* Priority Badge */}
                        {task.priority && (
                          <Chip
                            icon={<Flag />}
                            label={task.priority}
                            size="small"
                            color={getPriorityColor(task.priority)}
                            className="kanban-card__priority"
                          />
                        )}

                        {/* Title */}
                        <Typography variant="subtitle2" className="kanban-card__title">
                          {task.title}
                        </Typography>

                        {/* Description Preview */}
                        {task.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            className="kanban-card__description"
                          >
                            {task.description.substring(0, 60)}
                            {task.description.length > 60 ? '...' : ''}
                          </Typography>
                        )}

                        {/* Tags */}
                        {task.tags && task.tags.length > 0 && (
                          <Box className="kanban-card__tags">
                            {task.tags.slice(0, 3).map((tag, index) => (
                              <Chip
                                key={index}
                                label={tag}
                                size="small"
                                variant="outlined"
                                className="kanban-card__tag"
                              />
                            ))}
                          </Box>
                        )}

                        {/* Progress Bar */}
                        {progress && (
                          <Box className="kanban-card__progress">
                            <Typography variant="caption" color="text.secondary">
                              {progress.completed}/{progress.total} subtasks
                            </Typography>
                            <Box className="kanban-card__progress-bar">
                              <Box
                                className="kanban-card__progress-fill"
                                style={{ width: `${(progress.completed / progress.total) * 100}%` }}
                              />
                            </Box>
                          </Box>
                        )}

                        {/* Footer */}
                        <Box className="kanban-card__footer">
                          {/* Due Date */}
                          {task.dueDate && (
                            <Box className="kanban-card__due-date">
                              <AccessTime fontSize="small" />
                              <Typography variant="caption">
                                {new Date(task.dueDate).toLocaleDateString()}
                              </Typography>
                            </Box>
                          )}

                          {/* Story Points */}
                          {task.storyPoints && (
                            <Chip
                              label={`${task.storyPoints} SP`}
                              size="small"
                              variant="outlined"
                              className="kanban-card__story-points"
                            />
                          )}

                          {/* Assignee Avatar */}
                          {task.assigneeId && (() => {
                            const assigneeObj = (task as any).assignee;
                            const assigneeName = assigneeObj?.fullName || assigneeObj?.userName || null;
                            const initials = assigneeName
                              ? assigneeName
                                  .trim()
                                  .split(' ')
                                  .filter(Boolean)
                                  .slice(0, 2)
                                  .map((n: string) => n.charAt(0).toUpperCase())
                                  .join('')
                              : '?';
                            // Pick a color from assigneeId hash for consistent coloring
                            const colors = [
                              '#5b6af0', '#0073ea', '#22c58c', '#f5b942',
                              '#f0576b', '#8b7fd6', '#fb8332', '#00bcd4',
                            ];
                            const colorIdx = task.assigneeId
                              ? task.assigneeId.charCodeAt(0) % colors.length
                              : 0;
                            return (
                              <Avatar
                                className="kanban-card__avatar"
                                sx={{
                                  width: 26,
                                  height: 26,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  bgcolor: colors[colorIdx],
                                  color: '#fff',
                                  border: '2px solid rgba(255,255,255,0.15)',
                                  letterSpacing: 0,
                                  cursor: 'default',
                                  flexShrink: 0,
                                }}
                                title={assigneeName || `User ${task.assigneeId}`}
                              >
                                {initials}
                              </Avatar>
                            );
                          })()}

                          {/* Comments Count */}
                          {task.comments && task.comments.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {task.comments.length} 💬
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}

                {/* Empty State */}
                {!loading && columnTasks.length === 0 && (
                  <Box className="kanban-column__empty">
                    <InboxOutlined className="kanban-column__empty-icon" />
                    <Typography variant="body2" color="text.secondary">
                      No tasks
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {canScrollRight && (
        <IconButton
          className="kanban-board__scroll-btn kanban-board__scroll-btn--right"
          onClick={() => scrollColumns(1)}
          aria-label="Scroll columns right"
        >
          <ChevronRight />
        </IconButton>
      )}

      {/* Task Detail Dialog */}
      {selectedTask && (
        <TaskDetail
          // selectedTask only tracks WHICH task is open (and survives the
          // fade-out after close) - re-resolve it from the live `tasks`
          // prop on every render so an update made inside the dialog (e.g.
          // reassigning) is reflected immediately instead of the dialog
          // showing a stale snapshot captured at click-time.
          task={tasks.find((t) => t.id === selectedTask.id) || selectedTask}
          open={detailOpen}
          onClose={handleCloseDetail}
          onUpdate={onUpdateTask}
          epics={epics}
        />
      )}
    </Box>
  );
};

export default KanbanBoard;
